import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

export const SHORT_FORM_CONTENT_TYPE = {
  // ------------ Other ------------
  generalImage: 'general_image',
  generalText: 'general_text',

  // ------------ Social ------------
  facebookPost: 'facebook_post',
  instagramPost: 'instagram_caption',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  youtubeVideo: 'youtube_video',

  // ------------ Ads ------------
  facebookAd: 'facebook_ad',
  linkedinAd: 'linkedin_ad',
  twitterAd: 'twitter_ad',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  pinterestAd: 'pinterest_ad',
  outbrainAd: 'outbrain_ad',
  taboolaAd: 'taboola_ad',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',

  // ------------ Email ------------
  email: 'email',
  emailSubjectLine: 'email_subject_line',

  // ------------ Website ------------
  webpageHeadline: 'webpage_headline',
  webpageSubHeadline: 'webpage_sub_headline',
  blogHeadline: 'blog_headline',
  seoMetaDescription: 'seo_meta_description',

  // ------------ Ecommerce ------------
  amazonProductListing: 'amazon_product_listing',
  walmartProductListing: 'walmart_product_listing',
  generalProductListing: 'general_product_listing',
  productReview: 'product_review',
  sms: 'sms',
} as const;
export type ShortFormContentType =
  (typeof SHORT_FORM_CONTENT_TYPE)[keyof typeof SHORT_FORM_CONTENT_TYPE];

export const shortFormContentTypeHasScreenshotInput = (
  contentType: ShortFormContentType
): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.facebookPost,
    SHORT_FORM_CONTENT_TYPE.instagramPost,
    SHORT_FORM_CONTENT_TYPE.linkedinPost,
    SHORT_FORM_CONTENT_TYPE.twitterPost,
    SHORT_FORM_CONTENT_TYPE.facebookAd,
    SHORT_FORM_CONTENT_TYPE.linkedinAd,
    SHORT_FORM_CONTENT_TYPE.twitterAd,
    SHORT_FORM_CONTENT_TYPE.pinterestAd,
    SHORT_FORM_CONTENT_TYPE.outbrainAd,
    SHORT_FORM_CONTENT_TYPE.taboolaAd,
    SHORT_FORM_CONTENT_TYPE.linkedinTextAd,
  ];
  return types.includes(contentType);
};

export const shortFormContentTypeHasCSVInput = (contentType: ShortFormContentType): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

export const shortFormContentTypeHasSectionNumbering = (
  contentType: ShortFormContentType
): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

interface ShortFormContentTypeProps {
  title: string;
  generationTool: GenerationTool;
  withSingleAsset: boolean;
}

const SHORT_FORM_CONTENT_TYPE_PROPS: Record<ShortFormContentType, ShortFormContentTypeProps> = {
  // ------------ Other ------------
  [SHORT_FORM_CONTENT_TYPE.generalImage]: {
    title: 'Image',
    generationTool: GENERATION_TOOL.imageImprove,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.generalText]: {
    title: 'Text',
    generationTool: GENERATION_TOOL.basicContentImprover,
    withSingleAsset: false,
  },

  // ------------ Social ------------
  [SHORT_FORM_CONTENT_TYPE.facebookPost]: {
    title: 'Facebook Post',
    generationTool: GENERATION_TOOL.facebookPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.instagramPost]: {
    title: 'Instagram Post',
    generationTool: GENERATION_TOOL.instagramCaption,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinPost]: {
    title: 'LinkedIn Post',
    generationTool: GENERATION_TOOL.linkedinPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterPost]: {
    title: 'X Post',
    generationTool: GENERATION_TOOL.tweet,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.youtubeVideo]: {
    title: 'Youtube Video',
    generationTool: GENERATION_TOOL.youtubeMixturePost,
    withSingleAsset: false,
  },

  // ------------ Ads ------------
  [SHORT_FORM_CONTENT_TYPE.facebookAd]: {
    title: 'Meta Image ad',
    generationTool: GENERATION_TOOL.facebookMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinAd]: {
    title: 'LinkedIn Single Image ad',
    generationTool: GENERATION_TOOL.linkedinMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinConversationAd]: {
    title: 'LinkedIn Conversation ad',
    generationTool: GENERATION_TOOL.linkedinConversationAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinMessageAd]: {
    title: 'LinkedIn Message ad',
    generationTool: GENERATION_TOOL.linkedinMessageAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinLeadGenFormAd]: {
    title: 'LinkedIn Lead Gen Form ad',
    generationTool: GENERATION_TOOL.linkedinLeadGenFormAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinDocumentAd]: {
    title: 'LinkedIn Document ad',
    generationTool: GENERATION_TOOL.linkedinDocumentAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinTextAd]: {
    title: 'LinkedIn Text ad',
    generationTool: GENERATION_TOOL.linkedinTextAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinVideoAd]: {
    title: 'LinkedIn Video ad',
    generationTool: GENERATION_TOOL.linkedinVideoAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd]: {
    title: 'Google Performance Max ad',
    generationTool: GENERATION_TOOL.googlePerformanceMaxAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googleSearchAd]: {
    title: 'Google Search ad',
    generationTool: GENERATION_TOOL.googleRSA,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterAd]: {
    title: 'X Image Ad',
    generationTool: GENERATION_TOOL.twitterMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.pinterestAd]: {
    title: 'Pinterest Image Ad',
    generationTool: GENERATION_TOOL.pinterestMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.outbrainAd]: {
    title: 'Outbrain ad',
    generationTool: GENERATION_TOOL.outbrainAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.taboolaAd]: {
    title: 'Taboola ad',
    generationTool: GENERATION_TOOL.taboolaAd,
    withSingleAsset: false,
  },

  // ------------ Email ------------
  [SHORT_FORM_CONTENT_TYPE.email]: {
    title: 'Email',
    generationTool: GENERATION_TOOL.emailMixture,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.emailSubjectLine]: {
    title: 'Email - Subject line',
    generationTool: GENERATION_TOOL.emailSubjectLine,
    withSingleAsset: true,
  },

  // ------------ Website ------------
  [SHORT_FORM_CONTENT_TYPE.webpageHeadline]: {
    title: 'Webpage - Headline',
    generationTool: GENERATION_TOOL.landingHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageSubHeadline]: {
    title: 'Webpage - Subheadline',
    generationTool: GENERATION_TOOL.landingSubheader,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.blogHeadline]: {
    title: 'Blog - Headline',
    generationTool: GENERATION_TOOL.articleBlogHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.seoMetaDescription]: {
    title: 'SEO Meta Description',
    generationTool: GENERATION_TOOL.landingSEOMetadata,
    withSingleAsset: false,
  },

  // ------------ Ecommerce ------------
  [SHORT_FORM_CONTENT_TYPE.amazonProductListing]: {
    title: 'Amazon product listing',
    generationTool: GENERATION_TOOL.amazonProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.walmartProductListing]: {
    title: 'Walmart product listing',
    generationTool: GENERATION_TOOL.walmartProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.generalProductListing]: {
    title: 'Product Listing',
    generationTool: GENERATION_TOOL.generalProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.productReview]: {
    title: 'Product Review',
    generationTool: GENERATION_TOOL.basicProductReview,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.sms]: {
    title: 'SMS',
    generationTool: GENERATION_TOOL.smsPromotional,
    withSingleAsset: true,
  },
};

export const getShortFormContentTypeTitle = (contentType: ShortFormContentType): string =>
  SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.title || contentType;

export const getShortFormContentTypeTool = (contentType: ShortFormContentType): GenerationTool => {
  return SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.generationTool;
};

export const isSingleAssetRewriteContentType = (contentType: ShortFormContentType): boolean => {
  return contentType ? Boolean(SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.withSingleAsset) : false;
};
